import { useEffect, useState } from 'react'
import { Check, X } from 'lucide-react'
import { track, trackAfterPaint } from '../lib/analytics'
import { INTERESTS } from '../lib/useFilterGroup'

// First-visit interest picker (Andrew, 7/28). Asks what kind of events the
// visitor is after and applies the matching filter, so the first thing they see
// is their kind of week rather than all 170 rows.
//
// Shown ONCE per device: any exit (submit, X, backdrop, or "show me everything")
// writes the localStorage flag and it never comes back. Never shown to visitors
// arriving on a pre-filtered link (?group= / ?hosts=) - they already chose.
//
// One pick applies the full filter GROUP (banner + shareable ?group= URL, the
// same mechanism the Reddit/FB reply links use). Multiple picks union the
// groups' host categories into the checkbox filter instead - the group URL only
// carries one slug, and the drawer makes a multi-pick visible and adjustable.
// The options themselves are INTERESTS in lib/useFilterGroup - the drawer offers
// the same six, and they have to stay one list.
export const ONBOARDING_KEY = 'lyh_onboarding_v1'

interface Props {
  open: boolean
  /** Called with the chosen slugs (empty array = show everything). */
  onDone: (slugs: string[]) => void
}

export function OnboardingModal({ open, onDone }: Props) {
  const [picked, setPicked] = useState<Set<string>>(new Set())

  // After paint, not inline: this runs in the same task that first paints the
  // picker (the LCP element), and a capture here forced a 33-45 ms layout of
  // the whole page before it could paint (see trackAfterPaint). The cleanup
  // also means StrictMode's double-run in dev sends one event, not two.
  useEffect(() => {
    if (open) return trackAfterPaint('onboarding_shown')
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip('esc')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const toggle = (slug: string) => {
    setPicked((prev) => {
      const n = new Set(prev)
      if (n.has(slug)) n.delete(slug)
      else n.add(slug)
      // Every tap is a signal, selected or not - this is the demand data.
      track('onboarding_choice_toggled', { choice: slug, selecting: !prev.has(slug) })
      return n
    })
  }

  const submit = () => {
    const slugs = [...picked]
    track('onboarding_submitted', { choices: slugs, count: slugs.length })
    onDone(slugs)
  }

  const skip = (via: string) => {
    track('onboarding_skipped', { via, had_picked: picked.size })
    onDone([])
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => skip('backdrop')} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="What kind of events are you looking for?"
        className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-800 dark:bg-slate-900"
      >
        <button
          type="button"
          onClick={() => skip('x')}
          aria-label="Close"
          className="absolute right-1 top-1 inline-flex min-h-10 min-w-10 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 sm:min-h-0 sm:min-w-0 sm:p-1 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>

        <h2 className="pr-8 text-lg font-semibold text-slate-900 dark:text-slate-100">
          What kind of events are you looking for?
        </h2>
        <p className="mb-4 mt-1.5 text-sm text-slate-500 dark:text-slate-400">
          Pick any that fit and we&apos;ll filter the week for you. You can change it anytime.
        </p>

        <div className="grid grid-cols-2 gap-2">
          {INTERESTS.map((o) => {
            const on = picked.has(o.slug)
            return (
              <button
                key={o.slug}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(o.slug)}
                className={`relative rounded-lg border p-3 text-center transition-colors ${
                  on
                    ? 'border-brick-600 bg-brick-50 dark:border-brick-300 dark:bg-brick-900/20'
                    : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
                }`}
              >
                {on && (
                  <Check className="absolute right-2 top-2 h-4 w-4 text-brick-600 dark:text-brick-300" />
                )}
                {/* Padded on BOTH sides, not just the right: the tick sits in
                    the corner, and clearing it on one side only would push
                    centred text off centre. */}
                <span className="block px-5 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {o.label}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                  {o.desc}
                </span>
              </button>
            )
          })}
        </div>

        {/* Stacked, with the way past underneath the way forward (Andrew,
            7/28). Side by side they read as two equal choices; this reads as
            one action with an opt-out below it. The link takes the interest
            headings' colour so it stays legible rather than greyed out. */}
        <div className="mt-4 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={picked.size === 0}
            className="w-full rounded-md bg-brick-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brick-700 disabled:opacity-50"
          >
            Show my events
          </button>
          <button
            type="button"
            onClick={() => skip('show-everything')}
            className="text-sm text-slate-900 underline underline-offset-2 hover:text-brick-700 dark:text-slate-100 dark:hover:text-brick-300"
          >
            Show me everything
          </button>
        </div>
      </div>
    </div>
  )
}
